import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown } from "lucide-react";
import { JobFile } from "./admin/BookingDetail";
import { MessagesPanel } from "./admin/MessagesPanel";
import { ActivityPanel } from "./admin/ActivityPanel";
import { CalendarPanel } from "./admin/CalendarPanel";
import { LivePanel } from "./admin/LivePanel";
import { TrafficPanel } from "./admin/TrafficPanel";
import { EmailCenter } from "./admin/EmailCenter";
import { FinancialsPanel } from "./admin/FinancialsPanel";
import { ComplaintsPanel } from "./admin/ComplaintsPanel";
import { GalleryManager } from "./admin/GalleryManager";
import { StaffPanel } from "./admin/StaffPanel";
import { AdminLogin } from "./admin/AdminLogin";
import { DashboardTab } from "./admin/DashboardTab";
import { EnquiriesTab } from "./admin/EnquiriesTab";
import { SettingsTab } from "./admin/SettingsTab";
import { DiscountCodesPanel } from "./admin/DiscountCodesPanel";
import { TrashPanel } from "./admin/TrashPanel";
import { ClientsPanel } from "./admin/ClientsPanel";
import { RefundedPanel } from "./admin/RefundedPanel";
import { NotificationsBell } from "./admin/NotificationsBell";
import { GlobalSearch } from "./admin/GlobalSearch";

// Ops-critical tabs that stay on the main header. The rest sit in a "More"
// dropdown so the header doesn't clog up as the operations feature-set grows.
const MAIN_TAB_KEYS = new Set([
  "dashboard", "clients", "calendar", "messages", "enquiries", "complaints", "live", "settings",
]);

const TABS_BY_ROLE = {
  senior_admin: ["dashboard", "clients", "calendar", "messages", "enquiries", "complaints", "email", "financials", "gallery", "staff", "activity", "live", "traffic", "discounts", "refunded", "trash", "settings"],
  admin: ["dashboard", "clients", "calendar", "messages", "enquiries", "complaints", "email", "gallery", "staff", "activity", "live", "traffic", "discounts", "refunded", "settings"],
  office_staff: ["dashboard", "clients", "calendar", "messages", "enquiries", "complaints", "email", "gallery", "live", "traffic"],
  site_manager: ["dashboard", "clients"],
};
const TAB_LABEL = { dashboard: "Dashboard", live: "Live", traffic: "Traffic", clients: "Clients", calendar: "Calendar", messages: "Messages", enquiries: "Enquiries", complaints: "Complaints", email: "Email", financials: "Financials", gallery: "Gallery", staff: "Staff", activity: "Activity", discounts: "Discount codes", refunded: "Refunded", trash: "Trash", settings: "Settings" };
// Old bookmarked tab URLs land on the merged Clients tab.
const LEGACY_TABS = { bookings: "clients", customers: "clients", saved_quotes: "clients", dead_leads: "clients" };

export default function Admin() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab") || "dashboard";
    return LEGACY_TABS[t] || t;
  });
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [planningQuotes, setPlanningQuotes] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openRef, setOpenRef] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  const [openQuoteId, setOpenQuoteId] = useState(null);
  const [fileTick, setFileTick] = useState(0);
  const openBooking = (ref, sub = null) => { setOpenSub(sub); setOpenRef(ref); };
  const [counts, setCounts] = useState({ messages_unread: 0, overdue_reminders: 0, new_enquiries: 0 });
  const [attention, setAttention] = useState({ unread: 0, awaiting: 0 });
  const [clientsFilter, setClientsFilter] = useState("all");

  useEffect(() => {
    const check = async () => {
      try { const r = await api.get("/auth/me"); setMe(r.data); }
      catch { setMe(null); }
      finally { setLoading(false); }
    };
    check();
  }, []);

  const role = me?.role || "site_manager";
  const tabs = TABS_BY_ROLE[role] || ["dashboard", "clients"];
  const isAdminLevel = role === "admin" || role === "senior_admin";
  const isOfficeLevel = role === "office_staff" || isAdminLevel;

  const loadBookings = () => api.get("/admin/bookings").then(r => setBookings(r.data.items || [])).catch(() => {});
  const loadCounts = () => api.get("/admin/unread-count").then(r => setCounts(r.data)).catch(() => {});
  const loadAttention = () => api.get("/admin/needs-attention").then(r => setAttention(r.data.counts || { unread: 0, awaiting: 0 })).catch(() => {});
  const loadPlanningQuotes = () => api.get("/admin/planning-quotes").then(r => setPlanningQuotes(r.data.items || [])).catch(() => {});
  const loadComplaints = () => api.get("/admin/complaints").then(r => setComplaints(r.data.items || [])).catch(() => {});
  const loadEnquiries = () => api.get("/admin/enquiries").then(r => setEnquiries(r.data.items || [])).catch(() => {});

  useEffect(() => {
    if (!me) return;
    setTab(t => {
      if (tabs.includes(t)) return t;
      if (t === "settings") toast.error("You don't have access to pricing & settings");
      else if (t === "financials") toast.error("You don't have access to financials");
      return "dashboard";
    });
    loadBookings();
    loadCounts();
    loadAttention();
    const iv = setInterval(() => { loadCounts(); loadAttention(); }, 60000);
    if (isOfficeLevel) loadEnquiries();
    if (isOfficeLevel) { loadPlanningQuotes(); loadComplaints(); }
    if (isAdminLevel) api.get("/admin/settings").then(r => setSettings(r.data)).catch(() => {});
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when the signed-in admin (me) changes; loaders are stable closures
  }, [me]);

  const outstanding = (attention.unread || 0) + (attention.awaiting || 0);
  useEffect(() => {
    document.title = outstanding > 0 ? `(${outstanding}) Operations · The Extension Works` : "Operations · The Extension Works";
    return () => { document.title = "The Extension Works"; };
  }, [outstanding]);

  const mainTabs = tabs.filter(t => MAIN_TAB_KEYS.has(t));
  const moreTabs = tabs.filter(t => !MAIN_TAB_KEYS.has(t));
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDocClick = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [moreOpen]);
  const moreActive = moreTabs.includes(tab);

  if (loading) return <main className="px-6 py-24"><div className="label-eyebrow">Loading…</div></main>;

  if (!me) return <AdminLogin onLogin={setMe} />;

  const tabViews = {
    dashboard: () => (
      <DashboardTab counts={counts} bookings={bookings} isAdminLevel={isAdminLevel} isOfficeLevel={isOfficeLevel}
        setTab={setTab} setClientsFilter={setClientsFilter} loadCounts={loadCounts} setOpenRef={setOpenRef}
        openBooking={openBooking} onOpenQuote={setOpenQuoteId} />
    ),
    live: () => <LivePanel />,
    traffic: () => <TrafficPanel />,
    clients: () => (
      <ClientsPanel role={role} bookings={bookings} loadBookings={loadBookings} setOpenRef={setOpenRef}
        onCountsChange={loadCounts} filter={clientsFilter} setFilter={setClientsFilter} onOpenQuote={setOpenQuoteId}
        reloadKey={fileTick} />
    ),
    enquiries: () => (
      <EnquiriesTab enquiries={enquiries} planningQuotes={planningQuotes}
        loadEnquiries={loadEnquiries} loadPlanningQuotes={loadPlanningQuotes} loadCounts={loadCounts}
        setOpenRef={setOpenRef} loadBookings={loadBookings} />
    ),
    gallery: () => <GalleryManager />,
    messages: () => <MessagesPanel onOpenBooking={(ref) => setOpenRef(ref)} />,
    calendar: () => <CalendarPanel onOpenBooking={(ref) => setOpenRef(ref)} />,
    activity: () => <ActivityPanel onOpenBooking={(ref) => setOpenRef(ref)} />,
    staff: () => <StaffPanel meEmail={me.email} meRole={role} />,
    complaints: () => <ComplaintsPanel complaints={complaints} onReload={loadComplaints} role={role} />,
    email: () => <EmailCenter role={role} />,
    financials: () => <FinancialsPanel />,
    discounts: () => <DiscountCodesPanel />,
    refunded: () => <RefundedPanel onOpenBooking={(ref) => setOpenRef(ref)} />,
    trash: () => <TrashPanel onOpenBooking={(ref) => setOpenRef(ref)} />,
    settings: () => (settings ? <SettingsTab settings={settings} setSettings={setSettings} isAdminLevel={isAdminLevel} isSenior={role === "senior_admin"} /> : null),
  };

  return (
    <main data-testid="admin-page" className="px-4 md:px-8 pt-8 pb-24">
      <div className="flex justify-between items-end mb-6 gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Admin · {me.email} · <span className="text-[#C79A3B]">{role.replace(/_/g, " ")}</span></div>
          <h1 className="font-black tracking-tighter text-3xl md:text-4xl">Operations</h1>
        </div>
        <div className="flex items-center gap-2">
          {isOfficeLevel && <GlobalSearch onOpenBooking={(ref) => setOpenRef(ref)} onOpenQuote={setOpenQuoteId} />}
          <NotificationsBell onOpenQuote={setOpenQuoteId} />
          <button data-testid="admin-logout" onClick={async () => { await api.post("/auth/logout"); setMe(null); }} className="de-btn-secondary">Sign out</button>
        </div>
      </div>

      <div className="flex flex-wrap border-b border-[#E5E7EB] mb-6">
        {mainTabs.map(t => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => { setTab(t); if (t === "messages") setTimeout(loadCounts, 1200); }} className={`px-5 py-3 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2 ${tab === t ? "text-[#C79A3B] border-b-2 border-[#C79A3B]" : "text-[#6B7280]"}`}>
            {TAB_LABEL[t]}
            {t === "dashboard" && outstanding > 0 && (
              <span data-testid="dashboard-tab-badge" className="bg-[#C0392B] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{outstanding}</span>
            )}
            {t === "messages" && counts.messages_unread > 0 && (
              <span data-testid="messages-tab-badge" className="bg-[#C79A3B] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{counts.messages_unread}</span>
            )}
            {t === "enquiries" && counts.new_enquiries > 0 && (
              <span data-testid="enquiries-tab-badge" className="bg-[#C79A3B] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{counts.new_enquiries}</span>
            )}
            {t === "clients" && (counts.new_saved_quotes || 0) > 0 && (
              <span data-testid="clients-tab-badge" className="bg-[#C79A3B] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{counts.new_saved_quotes}</span>
            )}
          </button>
        ))}
        {moreTabs.length > 0 && (
          <div className="relative" ref={moreRef}>
            <button data-testid="tab-more"
              onClick={() => setMoreOpen(o => !o)}
              className={`px-5 py-3 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5 ${moreActive ? "text-[#C79A3B] border-b-2 border-[#C79A3B]" : "text-[#6B7280] hover:text-[#12263A]"}`}>
              {moreActive ? TAB_LABEL[tab] : "More"}
              <ChevronDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`}/>
            </button>
            {moreOpen && (
              <div data-testid="tab-more-dropdown" className="absolute top-full left-0 mt-0 min-w-[200px] bg-white border border-[#E5E7EB] shadow-lg z-40 py-1">
                {moreTabs.map(t => (
                  <button key={t} data-testid={`tab-more-${t}`}
                    onClick={() => { setTab(t); setMoreOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#FBF3EC] ${tab === t ? "text-[#C79A3B] bg-[#FBF3EC]" : "text-[#374151]"}`}>
                    {TAB_LABEL[t]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {isOfficeLevel && (
          <a href={`${api.defaults.baseURL}/admin/export/bookings.csv`} className="ml-auto text-xs uppercase tracking-widest text-[#6B7280] px-4 py-3 hover:text-[#C79A3B]">Export CSV</a>
        )}
      </div>

      {(tabViews[LEGACY_TABS[tab] || tab] || tabViews.dashboard)()}

      {openRef && <JobFile kind="booking" id={openRef} role={role} initialSub={openSub}
        onClose={() => { setOpenRef(null); setOpenSub(null); loadBookings(); setFileTick(t => t + 1); }} />}
      {!openRef && openQuoteId && <JobFile kind="quote" id={openQuoteId} role={role}
        onClose={() => { setOpenQuoteId(null); loadBookings(); setFileTick(t => t + 1); }} />}
    </main>
  );
}
